import React, { useCallback, useEffect, useRef, useState } from 'react';
import { handlePopupScroll } from '../helpers/on-popup-scroll';
import { initialQueryState, PaginationReturn } from '../interfaces/pagination';
import { FilterParams } from '../pages/AdministrationOfContinuousEducation/liste-prezenca-for-trainings.component';

export interface PaginatedDataConfig<T, Q extends object> {
  request: (params: Q & { page: number; size: number }) => {
    unwrap: () => Promise<PaginationReturn<T>>;
  };
  query?: Q;
  fetch?: boolean;
  fetchOn?: unknown[];
  resetOn?: unknown[];
  size?: number;
}

export function emptyPage<T>(): { unwrap: () => Promise<PaginationReturn<T>> } {
  return {
    unwrap: () =>
      Promise.resolve({
        currentPage: 0,
        totalPages: 1,
        totalElements: 0,
        data: [],
      }),
  };
}

export function usePaginatedData<T, Q extends object>({
  request,
  query,
  fetch = true,
  fetchOn,
  resetOn,
  size = 10,
}: PaginatedDataConfig<T, Q>) {
  const [data, setData] = useState<T[]>([]);
  const [internalQueryParams, setInternalQueryParams] =
    useState<FilterParams>(initialQueryState);
  const [resetSignal, setResetSignal] = useState(0);

  const requestRef = useRef(request);
  requestRef.current = request;

  const queryRef = useRef(query);
  queryRef.current = query;

  const sizeRef = useRef(size);
  sizeRef.current = size;

  const queryParamsRef = useRef(internalQueryParams);
  queryParamsRef.current = internalQueryParams;

  const prevResetOnRef = useRef<unknown[] | undefined>(undefined);

  useEffect(() => {
    const prev = prevResetOnRef.current;
    const curr = resetOn ?? [];

    if (prev === undefined) {
      prevResetOnRef.current = curr;
      return;
    }

    const changed =
      curr.length !== prev.length ||
      curr.some((v, i) => !Object.is(v, prev[i]));

    prevResetOnRef.current = curr;

    if (!changed) return;

    setData([]);
    setInternalQueryParams(initialQueryState);
    setResetSignal(s => s + 1);
  });
  const prevFetchOnRef = useRef<unknown[] | undefined>(undefined);
  const fetchSignalRef = useRef(0);
  const [fetchSignal, setFetchSignal] = useState(0);

  useEffect(() => {
    const prev = prevFetchOnRef.current;
    const curr = fetchOn ?? [];

    if (prev === undefined) {
      prevFetchOnRef.current = curr;
      return;
    }

    const changed =
      curr.length !== prev.length ||
      curr.some((v, i) => !Object.is(v, prev[i]));

    prevFetchOnRef.current = curr;

    if (changed) {
      fetchSignalRef.current += 1;
      setFetchSignal(fetchSignalRef.current);
    }
  });

  useEffect(() => {
    if (!fetch) return;
    const snapQuery = queryRef.current ?? ({} as Q);
    doFetch(internalQueryParams.page, snapQuery);
  }, [internalQueryParams.page, resetSignal, fetch, fetchSignal]);

  const doFetch = (page: number, snapQuery: Q) => {
    setInternalQueryParams(e => ({ ...e, loading: true }));
    requestRef
      .current({
        ...snapQuery,
        page,
        size: sizeRef.current,
      })
      .unwrap()
      .then(response => {
        const hasMore = response.currentPage < response.totalPages - 1;
        if (page === 0) {
          setData(response.data);
        } else {
          setData(prev => [...prev, ...response.data]);
        }
        setInternalQueryParams(e => ({ ...e, loading: false, hasMore }));
      })
      .catch(() =>
        setInternalQueryParams(e => ({
          ...e,
          loading: false,
          hasMore: false,
        })),
      );
  };

  const popupScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) =>
    handlePopupScroll(
      e,
      internalQueryParams.loading,
      internalQueryParams.hasMore,
      setInternalQueryParams,
    );

  const setQueryParams: React.Dispatch<React.SetStateAction<FilterParams>> =
    useCallback((action: React.SetStateAction<FilterParams>) => {
      const newState =
        typeof action === 'function' ? action(queryParamsRef.current) : action;
      if (newState.page === 0) {
        setData([]);
        setInternalQueryParams(initialQueryState);
        setResetSignal(s => s + 1);
      } else {
        setInternalQueryParams(newState);
      }
    }, []);

  return {
    data,
    loading: internalQueryParams.loading,
    hasMore: internalQueryParams.hasMore,
    queryParams: internalQueryParams,
    popupScroll,
    setQueryParams,
  };
}
